import React, { useState } from 'react';
import { AuthProvider, useAuth } from '../contexts/AuthContext';
import LoginForm from './auth/LoginForm';
import Sidebar from './layout/Sidebar';
import PageManager from './pages/PageManager';
import Dashboard from './dashboard/Dashboard';
import MediaLibrary from './media/MediaLibrary';
import UserManager from './users/UserManager';
import Settings from './settings/Settings';
import AutoRAGChat from './autorag/AutoRAGChat';
import NavigationManager from './navigation/NavigationManager';
import PageEditor from './pages/PageEditor';
import { Page, PageFormData } from '../types/cms';
// import { Menu } from 'lucide-react';
import '../styles.css';

interface CMSMainProps {}

const CMSMain: React.FC<CMSMainProps> = () => {
  const { user, loading } = useAuth();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [editingPage, setEditingPage] = useState<Page | null>(null);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading MotoManual CMS...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginForm />;
  }

  const handleEditPage = (page: Page) => {
    setEditingPage(page.id ? page : null);
    setActiveSection('page-editor');
  };

  const handlePageSaved = async (data: PageFormData): Promise<void> => {
    try {
      // TODO: Save page data to API
      console.log('Saving page data:', data);
      setEditingPage(null);
      setActiveSection('pages');
    } catch (error) {
      console.error('Error saving page:', error);
    }
  };

  const handlePageCancelled = () => {
    setEditingPage(null);
    setActiveSection('pages');
  };

  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return <Dashboard />;
      case 'pages':
        return <PageManager onEditPage={handleEditPage} />;
      case 'page-editor':
        return (
          <PageEditor 
            page={editingPage || undefined}
            onSave={handlePageSaved}
            onCancel={handlePageCancelled}
            templates={[]} // TODO: Load templates
            loading={false}
          />
        );
      case 'media':
        return <MediaLibrary />;
      case 'navigation':
        return <NavigationManager />;
      case 'autorag':
        return <AutoRAGChat />;
      case 'users':
        return user.role === 'admin' ? <UserManager /> : <div>Access denied</div>;
      case 'settings':
        return user.role === 'admin' ? <Settings /> : <div>Access denied</div>;
      default:
        return <Dashboard />;
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <Sidebar 
        activeSection={activeSection}
        onSectionChange={setActiveSection}
        isMobileOpen={isMobileMenuOpen}
        onMobileToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="bg-white shadow-sm border-b border-gray-200 lg:hidden">
          <div className="flex items-center justify-between px-4 py-3">
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div className="text-lg font-semibold text-gray-900">
              MotoManual CMS
            </div>
            <div className="w-10"></div> {/* Spacer for centering */}
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-auto">
          <div className="p-6">
            {renderContent()}
          </div>
        </main>
      </div>
    </div>
  );
};

const MotoManualCMS: React.FC = () => {
  return (
    <AuthProvider>
      <CMSMain />
    </AuthProvider>
  );
};

export default MotoManualCMS;
